/**
 * EditBotModal — редактирование оформления бота зарплаты (admin-only).
 * Поддерживает: display_name + avatar (upload файл / pack-иконка из 100 / убрать).
 */
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BOT_SALARY_USER_ID, updateBotProfile, uploadBotAvatar, deleteBotAvatar,
  setBotAvatarFromPack, userAvatarUrl,
} from "@/api/dashboard";
import { PACK_ICON_COUNT, packIconUrl } from "@/data/animatedIcons";

function initialsFor(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

type AvatarChoice =
  | { kind: "keep" }
  | { kind: "file"; file: File; previewUrl: string }
  | { kind: "pack"; index: number }
  | { kind: "remove" };

export function EditBotModal({
  currentDisplayName,
  currentAvatarFilename,
  onClose,
}: {
  currentDisplayName: string;
  currentAvatarFilename: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(currentDisplayName);
  const [choice, setChoice] = useState<AvatarChoice>({ kind: "keep" });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (trimmed && trimmed !== currentDisplayName) {
        await updateBotProfile({ display_name: trimmed });
      }
      if (choice.kind === "remove") {
        await deleteBotAvatar();
      } else if (choice.kind === "file") {
        await uploadBotAvatar(choice.file);
      } else if (choice.kind === "pack") {
        await setBotAvatarFromPack(choice.index);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat/topics"] });
      qc.invalidateQueries({ queryKey: ["users/public"] });
      onClose();
    },
  });

  // Preview src
  let previewSrc: string | null = null;
  if (choice.kind === "file") previewSrc = choice.previewUrl;
  else if (choice.kind === "pack") previewSrc = packIconUrl(choice.index);
  else if (choice.kind === "keep") previewSrc = userAvatarUrl(BOT_SALARY_USER_ID, currentAvatarFilename);
  // remove → null → инициалы

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = typeof reader.result === "string" ? reader.result : "";
      setChoice({ kind: "file", file: f, previewUrl: url });
    };
    reader.readAsDataURL(f);
  };

  return (
    <div
      className="mng-modal-overlay show"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="mng-modal" onClick={(e) => e.stopPropagation()}>
        <header className="mng-modal-header">
          <span className="mng-modal-title">Оформление бота</span>
          <button className="mng-modal-close" onClick={onClose} title="Закрыть">✕</button>
        </header>

        <div className="mng-modal-body">
          {/* === Avatar preview + actions === */}
          <div className="mng-modal-group">
            <div className="mng-avatar-upload">
              {previewSrc ? (
                <img
                  className="mng-avatar mng-avatar-lg"
                  src={previewSrc}
                  alt=""
                  style={{
                    objectFit: choice.kind === "pack" ? "contain" : "cover",
                    background: choice.kind === "pack" ? "transparent" : undefined,
                  }}
                />
              ) : (
                <div
                  className="mng-avatar mng-avatar-lg"
                  style={{ background: "var(--brick, #b54a35)" }}
                >
                  {initialsFor(name)}
                </div>
              )}
              <div className="mng-avatar-hint">
                <label className="mng-avatar-upload-btn">
                  Загрузить фото
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onPickFile}
                  />
                </label>
                {(previewSrc || currentAvatarFilename) && (
                  <button
                    type="button"
                    className="mng-avatar-remove"
                    onClick={() => setChoice({ kind: "remove" })}
                  >Убрать</button>
                )}
              </div>
            </div>
          </div>

          {/* === Name === */}
          <div className="mng-modal-group">
            <label className="mng-modal-label">Имя бота</label>
            <input
              type="text"
              className="mng-modal-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>

          {/* === Pack-иконки (100 шт., 3D-emoji) === */}
          <div className="mng-modal-group">
            <label className="mng-modal-label">Иконка из пака (если нет фото)</label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))",
                gap: 6,
                maxHeight: 240,
                overflowY: "auto",
                padding: 4,
              }}
            >
              {Array.from({ length: PACK_ICON_COUNT }, (_, i) => i + 1).map(n => {
                const isActive = choice.kind === "pack" && choice.index === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setChoice({ kind: "pack", index: n })}
                    style={{
                      width: 48, height: 48,
                      border: isActive ? "2px solid var(--brick, #b54a35)" : "1px solid transparent",
                      borderRadius: 10,
                      background: isActive ? "rgba(181, 74, 53, 0.08)" : "transparent",
                      padding: 4,
                      cursor: "pointer",
                    }}
                    title={`Иконка ${n}`}
                  >
                    <img
                      src={packIconUrl(n)}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      loading="lazy"
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {saveMut.isError && (
            <div className="mng-modal-group" style={{ color: "#e85c5c" }}>
              Не удалось сохранить
            </div>
          )}
        </div>

        <div className="mng-modal-footer">
          <button className="mng-btn mng-btn-secondary" onClick={onClose}>Отмена</button>
          <button
            className="mng-btn mng-btn-primary"
            disabled={!name.trim() || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >{saveMut.isPending ? "Сохранение…" : "Сохранить"}</button>
        </div>
      </div>
    </div>
  );
}
